import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Typography,
  InputLabel,
  FormControl,
  OutlinedInput,
  TextField,
  Checkbox,
  ListItemText,
  Divider,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateTestCasesUsingFSDAndUseCase,
  generateTraceabilityMatrix,
} from "../../../services/webServices";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../MultiTestCaseGenerationPage";
import { LoadingModalContext } from "../ServicePage";
 
const MultipleSelectTestingTypesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setTestCaseLoaderState } = useContext(TestCasesLoaderContext);
  const {
    functionalSpecDocument,
    multipleUseCases,
    selectedUseCases,
    multipleUserStories,
    selectedUserStories,
    selectedTestingTypes,
    setSelectedTestingTypes,
    testConditions,
    testingTechniques,
  } = useContext(MultipleTestCasesContext);
 
  const STATIC_TESTING_CATEGORIES = [
    { name: "Positive test case", type: "functional" },
    { name: "Negative test case", type: "functional" },
    { name: "Boundary test case", type: "functional" },
    { name: "Data validation case", type: "functional" },
    { name: "Error handling case", type: "functional" },
    { name: "Performance test case", type: "non-functional" },
    { name: "Security test case", type: "non-functional" },
    { name: "Usability test case", type: "non-functional" },
    { name: "Accessibility test case", type: "non-functional" },
    { name: "Compatibility test case", type: "non-functional" },
    { name: "Other", type: "custom" },
  ];
 
  const [allCategories, setAllCategories] = useState(STATIC_TESTING_CATEGORIES);
  const [customTypeName, setCustomTypeName] = useState("");
  const [customTypeCategory, setCustomTypeCategory] = useState("functional");
  const [dropdownOpen, setDropdownOpen] = useState(false);
 
  // Track group selections
  const [functionalSelected, setFunctionalSelected] = useState(false);
  const [nonFunctionalSelected, setNonFunctionalSelected] = useState(false);
  const [otherSelected, setOtherSelected] = useState(false);
 
  useEffect(() => {
    setTestCaseLoaderState?.("Select Testing Types");
    setAllCategories(STATIC_TESTING_CATEGORIES);
  }, []);
 
  // Handle selecting group checkboxes
  const handleGroupSelect = (group) => {
    if (group === "functional") {
      const funcTypes = allCategories.filter((c) => c.type === "functional");
      if (functionalSelected) {
        // unselect all functional
        setSelectedTestingTypes((prev) =>
          prev.filter((c) => c.type !== "functional")
        );
        setFunctionalSelected(false);
      } else {
        // add all functional
        const merged = [
          ...selectedTestingTypes.filter((c) => c.type !== "functional"),
          ...funcTypes,
        ];
        setSelectedTestingTypes(merged);
        setFunctionalSelected(true);
      }
    }
 
    if (group === "non-functional") {
      const nonFuncTypes = allCategories.filter(
        (c) => c.type === "non-functional"
      );
      if (nonFunctionalSelected) {
        setSelectedTestingTypes((prev) =>
          prev.filter((c) => c.type !== "non-functional")
        );
        setNonFunctionalSelected(false);
      } else {
        const merged = [
          ...selectedTestingTypes.filter((c) => c.type !== "non-functional"),
          ...nonFuncTypes,
        ];
        setSelectedTestingTypes(merged);
        setNonFunctionalSelected(true);
      }
    }
 
    if (group === "other") {
  if (otherSelected) {
    // Unselect "Other" checkbox
    setOtherSelected(false);
    // Reset custom type input
    setCustomTypeName("");
    setCustomTypeCategory("functional");
  } else {
    // Select "Other" checkbox to show input
    setOtherSelected(true);
  }
}
 
  };
 
  const handleDeleteChip = (cat) => {
    setSelectedTestingTypes((prev) =>
      prev.filter((c) => !(c.name === cat.name && c.type === cat.type))
    );
    if (cat.type === "functional") setFunctionalSelected(false);
    if (cat.type === "non-functional") setNonFunctionalSelected(false);
    if (cat.name === "Other") {
      setOtherSelected(false);
      setCustomTypeName("");
      setCustomTypeCategory("functional");
    }
  };
 
  const handleContinue = () => setDropdownOpen(false);
 
  // Add custom category
  const handleAddCustomType = () => {
    const name = (customTypeName || "").trim();
    if (!name) return;
 
    const newItem = { name, type: customTypeCategory };
 
    if (
      !allCategories.find(
        (c) =>
          c.name.toLowerCase() === name.toLowerCase() &&
          c.type === customTypeCategory
      )
    ) {
      setAllCategories((prev) => [...prev, newItem]);
    }
 
    setSelectedTestingTypes([...(selectedTestingTypes || []), newItem]);
 
    setCustomTypeName("");
    setCustomTypeCategory("functional");
  };
 
  const handleGenerateMultipleTestCases = async () => {
    let progressHandler;
    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 8000,
      });
      progressHandler.start("Generating Test Cases");
 
      const useCasesToProcess =
        Array.isArray(selectedUseCases) &&
        selectedUseCases.filter(Boolean).length > 0
          ? selectedUseCases.filter(Boolean)
          : Array.isArray(multipleUseCases)
          ? multipleUseCases.filter(Boolean)
          : [];
 
      if (!useCasesToProcess.length) {
        setLoadingState({
          open: true,
          message: "Please select at least one Use Case",
          progress: 0,
        });
        setTimeout(
          () =>
            setLoadingState({ open: false, message: "", progress: null }),
          1200
        );
        return;
      }

      if (!testConditions || Object.keys(testConditions).length === 0) {
        setLoadingState({
          open: true,
          message: "Test Conditions are required",
          progress: 0,
        });
        setTimeout(
          () => setLoadingState({ open: false, message: "", progress: null }),
          1200
        );
        return;
      }

      if (!testingTechniques || Object.keys(testingTechniques).length === 0) {
        setLoadingState({
          open: true,
          message: "Testing Techniques are required",
          progress: 0,
        });
        setTimeout(
          () => setLoadingState({ open: false, message: "", progress: null }),
          1200
        );
        return;
      }
 
      setTestCaseLoaderState("Test Cases");
 
      const selectedCategoriesPayload = selectedTestingTypes.map((c) => ({
        name: c.name,
        type: c.type,
      }));
 
      const testCasePromises = useCasesToProcess.map(async (eachUseCase) => {
        if (eachUseCase && Object.keys(eachUseCase).length > 0) {
          const requestData = {
            testConditions: testConditions,
            // testingTechniques: testingTechniques,
            useCases: eachUseCase,
            selectedCategories: selectedCategoriesPayload,
          };
          return generateTestCasesUsingFSDAndUseCase(requestData);
        }
        return null;
      });
 
      const responses = await Promise.all(testCasePromises);
      const multipleTestCasesArray = responses
        .filter((r) => r?.data?.testcases)
        .map((r) => r.data.testcases);
      const flattenedTestCases = multipleTestCasesArray.flat();
 
      const testCasesDetailsForRTM = flattenedTestCases.map((t) => ({
        "Test Case ID": t["Test Case ID"],
        "Test Case Title": t["Test Case Title"],
        "Test Objective": t["Test Objective"],
      }));
 
      const traceabilityMatrix = await generateTraceabilityMatrix({
        functionalSpecDocument: functionalSpecDocument["Functional Requirements"],
        testCaseDetails: testCasesDetailsForRTM,
      });
 
      await progressHandler.finishAndClose();
 
      navigate("/dashboard/generate-multi/testcases/output", {
        state: {
          ...location.state,
          userStories: multipleUserStories,
          selectedUserStories,
          multipleUseCases,
          selectedMultipleUseCases: useCasesToProcess,
          functionalSpecDocument,
          testConditions,
          testingTechniques,
          multipleTestCases: flattenedTestCases,
          selectedTestingTypes,
          traceabilityMatrix: traceabilityMatrix.data || {},
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error", error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setTestCaseLoaderState(null);
      if (progressHandler) progressHandler.stop();
      setLoadingState({ open: false, message: "", progress: null });
    }
  };
 
  return (
    <Paper
      sx={{
        maxWidth: 600,
        margin: "16px auto",
        p: 2,
        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        borderRadius: 8,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, color: "#000" }}>
        Select Testing Types
      </Typography>
 
      {/* Dropdown */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="multi-testing-types-label" sx={{ color: "#a0a0a0" }}>
          Testing Types
        </InputLabel>
        <Select
          labelId="multi-testing-types-label"
          multiple
          open={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          onOpen={() => setDropdownOpen(true)}
          value={(selectedTestingTypes || []).map(
            (c) => `${c.name}-${c.type}`
          )}
          input={<OutlinedInput label="Testing Types" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected
                .map((value) =>
                  allCategories.find((c) => `${c.name}-${c.type}` === value)
                )
                .filter(Boolean)
                .map((cat) => (
                  <Chip
                    key={`${cat.name}-${cat.type}`}
                    label={`${cat.name} (${cat.type})`}
                    onDelete={() => handleDeleteChip(cat)}
                    deleteIcon={
                      <span
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      >
                        ✖
                      </span>
                    }
                  />
                ))}
            </Box>
          )}
        >
          {/* Functional Group */}
          <MenuItem>
            <Checkbox
              checked={functionalSelected}
              onChange={() => handleGroupSelect("functional")}
            />
            <ListItemText primary="Functional" />
          </MenuItem>
 
          {/* Non-Functional Group */}
          <MenuItem>
            <Checkbox
              checked={nonFunctionalSelected}
              onChange={() => handleGroupSelect("non-functional")}
            />
            <ListItemText primary="Non-Functional" />
          </MenuItem>
 
          {/* Other */}
          <MenuItem>
            <Checkbox
              checked={otherSelected}
              onChange={() => handleGroupSelect("other")}
            />
            <ListItemText primary="Other" />
          </MenuItem>
 
          <Divider />
          <MenuItem disableRipple>
            <Button
              fullWidth
              variant="contained"
              onClick={handleContinue}
              sx={{
                textTransform: "none",
                backgroundColor: "#2196F3",
                color: "#fff",
                "&:hover": { backgroundColor: "#1976D2" },
              }}
            >
              Continue
            </Button>
          </MenuItem>
        </Select>
      </FormControl>
 
      {/* Custom Category input for "Other" */}
      {otherSelected && (
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            label="Category"
            value={customTypeName}
            onChange={(e) => setCustomTypeName(e.target.value)}
            fullWidth
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel
              id="custom-type-category-label"
              sx={{ color: "#a0a0a0" }}
            >
              Type
            </InputLabel>
            <Select
              labelId="custom-type-category-label"
              value={customTypeCategory}
              onChange={(e) => setCustomTypeCategory(e.target.value)}
            >
              <MenuItem value="functional">functional</MenuItem>
              <MenuItem value="non-functional">non-functional</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleAddCustomType}>
            Add
          </Button>
        </Box>
      )}
 
      {/* Generate Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button 
          variant="contained" 
          onClick={handleGenerateMultipleTestCases}
          disabled={
            !selectedTestingTypes || 
            selectedTestingTypes.length === 0 ||
            !testConditions || 
            Object.keys(testConditions).length === 0 ||
            !testingTechniques || 
            Object.keys(testingTechniques).length === 0
          }
          sx={{
            opacity: (
              !selectedTestingTypes || 
              selectedTestingTypes.length === 0 ||
              !testConditions || 
              Object.keys(testConditions).length === 0 ||
              !testingTechniques || 
              Object.keys(testingTechniques).length === 0
            ) ? 0.6 : 1,
            cursor: (
              !selectedTestingTypes || 
              selectedTestingTypes.length === 0 ||
              !testConditions || 
              Object.keys(testConditions).length === 0 ||
              !testingTechniques || 
              Object.keys(testingTechniques).length === 0
            ) ? "not-allowed" : "pointer",
          }}
        >
          Generate Test Cases
        </Button>
      </Box>
    </Paper>
  );
};
 
export default MultipleSelectTestingTypesPage;
 